import { FC } from "react"
import { Formik } from "formik"
import { object, string } from "yup"
import { RateFlowDetails } from "../../../types"
import ManageRateflowFormContent from "../../../common/ui/config-admin/config-rateflow/manage/form-content"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import Tabs from "../../../common/ui/tabs"
import {
  useFindRateflowDetailsQuery,
  useUpdateOrCreateRateflowMutation,
  useDeleteRateflowMutation,
  useFetchLoanOfficerQuery
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useAppSelector } from "../../../common/hooks"
import { selectUserDetails } from "../../../redux/slices/auth"
import { useConfigEditPage } from "../use-config-edit-page"
import { buildPageTitle } from "../utils"

const TABS = [
  { id: "general", label: "General" },
  { id: "pricing-engine", label: "Pricing Engine" },
  { id: "lo-map", label: "LO Map" }
]

const RateflowSchema = object({
  name: string().required().min(1),
  default: string().required(),
  bank_id: string().required().min(1),
  settings: object().required()
})

const RateflowEditPage: FC = () => {
  const user = useAppSelector(selectUserDetails)
  const [updateOrCreate, saveResult] = useUpdateOrCreateRateflowMutation()
  const [deleteRateflow, deleteResult] = useDeleteRateflowMutation()

  const {
    id,
    isCreate,
    formRef,
    setFormField,
    tab,
    setTab,
    brands,
    navigateBack
  } = useConfigEditPage<RateFlowDetails>({
    tabs: TABS,
    tab: "rateflow",
    label: "rateflow",
    saveResult,
    deleteResult,
    brandIdField: "bank_id"
  })

  const { data: rateflowData, isLoading } = useFindRateflowDetailsQuery(
    { id: id ?? "" },
    { skip: isCreate || !id }
  )

  const ownerId = rateflowData?.owner ?? ""
  const { data: ownerLo } = useFetchLoanOfficerQuery(ownerId, {
    skip: isCreate || !ownerId
  })

  const raw = rateflowData ?? {
    name: "",
    id: "",
    active: "1",
    default: "0",
    created: "",
    updated: "",
    bank_id: "",
    loid: user?.id ?? "",
    owner: user?.id ?? "",
    pricing_set_id: "0",
    settings: {}
  }

  const initialValues: RateFlowDetails = {
    ...raw,
    name: raw.name || "",
    active: raw.active ?? "1",
    default: raw.default || "0",
    settings: raw.settings ?? {}
  }

  const onSubmit = () => {
    const vals = formRef.current?.values
    if (!vals) return
    if (!isCreate && vals.id !== id) return
    updateOrCreate(vals)
  }

  const onDelete = () => {
    if (rateflowData?.id) deleteRateflow(rateflowData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={3} />
  }

  if (!isCreate && !isLoading && !rateflowData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50">
        <p className="text-sm text-gray-500">Rateflow not found</p>
        <button
          type="button"
          className="text-sm text-blue-600 hover:underline"
          onClick={() => navigateBack()}
        >
          Back to Rateflows
        </button>
      </div>
    )
  }

  const brandName = brands?.find(b => b.id === raw.bank_id)?.name
  const ownerName = ownerLo?.name
  const pageTitle = buildPageTitle(isCreate, "Rateflow", brandName)

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={RateflowSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              allowEditName
              backLabel="Rateflows"
              namePlaceholder="Rateflow name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty}
              isDefault={values.default === "1"}
              isEnabled={values.active === "1"}
              brands={brands ?? []}
              brandId={values.bank_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("default", v ? "1" : "0")}
              onEnabledChange={v => setFormField("active", v ? "1" : "0")}
              onBrandChange={val => setFormField("bank_id", val)}
              onDelete={onDelete}
              onCancel={() => navigateBack(values.bank_id)}
              onSubmit={onSubmit}
              onBack={() => navigateBack(values.bank_id)}
              saving={saveResult.isLoading}
              meta={
                !isCreate ? (
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {[
                      { label: "Rateflow ID", value: raw.id },
                      { label: "Pricing Set", value: raw.pricing_set_id },
                      {
                        label: "Owner",
                        value: ownerName
                          ? `${ownerName} (${raw.owner})`
                          : raw.owner
                      },
                      { label: "LO ID", value: raw.loid },
                      {
                        label: "Bank",
                        value: brandName
                          ? `${brandName} (${raw.bank_id})`
                          : raw.bank_id
                      }
                    ].map(({ label, value }) => (
                      <span key={label} className="text-sm text-neutral-500">
                        <span className="font-medium text-neutral-400">
                          {label}:
                        </span>{" "}
                        {value}
                      </span>
                    ))}
                  </div>
                ) : undefined
              }
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageRateflowFormContent
                  values={values}
                  tab={tab}
                  setFormField={setFormField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default RateflowEditPage
